export interface AzureADTokens {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

export interface AzureADUser {
  id: string;
  displayName: string;
  email: string;
  photoURL?: string;
  organizationId?: string;
  organizationName?: string;
}

export interface AzureADConfig {
  clientId: string;
  tenantId: string;
  redirectUri: string;
  scopes: string[];
}

export class AzureADDirectService {
  private config: AzureADConfig;

  constructor(config: AzureADConfig) {
    this.config = config;
  }

  /**
 * Build the Azure AD authorization URL with implicit flow
 */
  async buildAuthURL(state?: string): Promise<string> {
    const baseUrl = `https://login.microsoftonline.com/${this.config.tenantId}/oauth2/v2.0/authorize`;

    const params = new URLSearchParams({
      client_id: this.config.clientId,
      response_type: 'id_token token',
      redirect_uri: this.config.redirectUri,
      scope: this.config.scopes.join(' '),
      response_mode: 'fragment',
      prompt: 'select_account',
      state: state || this.generateState(),
      nonce: this.generateNonce()
    });

    return `${baseUrl}?${params.toString()}`;
  }

  /**
   * Get user information from Microsoft Graph
   */
  async getUserInfo(accessToken: string): Promise<AzureADUser> {
    const response = await fetch('https://graph.microsoft.com/v1.0/me', {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get user info: ${response.status} ${response.statusText}`);
    }

    const userData = await response.json();

    return {
      id: userData.id,
      displayName: userData.displayName,
      email: userData.mail || userData.userPrincipalName,
      photoURL: undefined, // Will be fetched separately if needed
    };
  }

  /**
   * Get user photo from Microsoft Graph
   */
  async getUserPhoto(accessToken: string): Promise<string | null> {
    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        }
      });

      if (response.ok) {
        const photoBlob = await response.blob();
        return URL.createObjectURL(photoBlob);
      }

      return null;
    } catch (error) {
      console.warn('Failed to fetch user photo:', error);
      return null;
    }
  }

  /**
   * Exchange authorization code for tokens with PKCE
   */
  async exchangeCodeForTokens(code: string): Promise<AzureADTokens> {
    const tokenUrl = `https://login.microsoftonline.com/${this.config.tenantId}/oauth2/v2.0/token`;

    // Get the stored code verifier
    const codeVerifier = sessionStorage.getItem('azuread.code_verifier');
    if (!codeVerifier) {
      throw new Error('No code verifier found. Please try signing in again.');
    }

    const body = new URLSearchParams({
      client_id: this.config.clientId,
      scope: this.config.scopes.join(' '),
      code: code,
      redirect_uri: this.config.redirectUri,
      grant_type: 'authorization_code',
      code_verifier: codeVerifier
    });

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      },
      body: body.toString()
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[AzureADDirectService] Token exchange error response:', errorText);
      throw new Error(`Token exchange failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    // Clear the code verifier after successful exchange
    sessionStorage.removeItem('azuread.code_verifier');

    return await response.json();
  }



  /**
   * Extract error from URL
   */
  extractErrorFromURL(url: string): { error: string; error_description?: string } | null {
    const urlObj = new URL(url);
    const error = urlObj.searchParams.get('error');
    const errorDescription = urlObj.searchParams.get('error_description');

    if (error) {
      return {
        error,
        error_description: errorDescription || undefined
      };
    }

    return null;
  }

  /**
   * Extract authorization code from URL (authorization code flow)
   */
  extractCodeFromURL(url: string): string | null {
    try {
      console.log('[AzureADDirectService] Extracting authorization code from URL:', url);
      const urlObj = new URL(url);

      // Check if there's an error in the URL
      const error = urlObj.searchParams.get('error');
      if (error) {
        console.error('[AzureADDirectService] Azure AD error:', error);
        const errorDescription = urlObj.searchParams.get('error_description');
        console.error('[AzureADDirectService] Error description:', errorDescription);
        return null;
      }

      // Extract authorization code from query parameters
      const code = urlObj.searchParams.get('code');
      if (code) {
        console.log('[AzureADDirectService] ✅ Authorization code found');
        return code;
      }

      console.log('[AzureADDirectService] ❌ No authorization code found in URL');
      return null;
    } catch (error) {
      console.error('[AzureADDirectService] Error extracting authorization code:', error);
      return null;
    }
  }

  /**
   * Extract tokens from URL (supports both hash fragments and query parameters)
   * This is kept for backward compatibility but not used in authorization code flow
   */
  extractTokensFromURL(url: string): AzureADTokens | null {
    try {
      // Check if we're in debug mode (web app only)
      const isDebugMode = typeof window !== 'undefined' && (process.env.REACT_APP_DEBUG === 'true' || process.env.REACT_APP_DEBUG === '1');

      if (isDebugMode) {
        console.log('[AzureADDirectService] Extracting tokens from URL:', url);
      } else {
        console.log('[AzureADDirectService] Extracting tokens from URL (URL hidden)');
      }

      const urlObj = new URL(url);

      let params: URLSearchParams;
      let source: string;

      // Check if tokens are in hash fragment
      if (urlObj.hash && urlObj.hash.includes('access_token=')) {
        const fragment = urlObj.hash.substring(1);
        params = new URLSearchParams(fragment);
        source = 'hash fragment';
        if (isDebugMode) {
          console.log('[AzureADDirectService] Using hash fragment:', fragment);
        } else {
          console.log('[AzureADDirectService] Using hash fragment (content hidden)');
        }
      }
      // Check if tokens are in query parameters
      else if (urlObj.search && urlObj.search.includes('access_token=')) {
        params = new URLSearchParams(urlObj.search);
        source = 'query parameters';
        if (isDebugMode) {
          console.log('[AzureADDirectService] Using query parameters:', urlObj.search);
        } else {
          console.log('[AzureADDirectService] Using query parameters (content hidden)');
        }
      }
      else {
        console.log('[AzureADDirectService] ❌ No tokens found in URL');
        return null;
      }

      console.log('[AzureADDirectService] Token source:', source);

      if (isDebugMode) {
        console.log('[AzureADDirectService] Params:', Object.fromEntries(params.entries()));
      } else {
        console.log('[AzureADDirectService] Params: [content hidden]');
      }

      const accessToken = params.get('access_token');
      const idToken = params.get('id_token');
      const expiresIn = params.get('expires_in');
      const tokenType = params.get('token_type');

      console.log('[AzureADDirectService] Extracted values:');
      console.log('[AzureADDirectService]   - access_token:', accessToken ? 'present' : 'missing');
      console.log('[AzureADDirectService]   - id_token:', idToken ? 'present' : 'missing');
      console.log('[AzureADDirectService]   - expires_in:', expiresIn);
      console.log('[AzureADDirectService]   - token_type:', tokenType);

      if (!accessToken || !idToken || !expiresIn || !tokenType) {
        console.log('[AzureADDirectService] ❌ Missing required tokens');
        return null;
      }

      console.log('[AzureADDirectService] ✅ All tokens present');
      return {
        access_token: accessToken,
        id_token: idToken,
        expires_in: parseInt(expiresIn, 10),
        token_type: tokenType
      };
    } catch (error) {
      console.error('[AzureADDirectService] Error extracting tokens from URL:', error);
      return null;
    }
  }

  /**
 * Complete authentication flow
 */
  async completeAuth(url: string): Promise<{ user: AzureADUser; tokens: AzureADTokens }> {
    // Check for errors first
    const error = this.extractErrorFromURL(url);
    if (error) {
      throw new Error(`Azure AD error: ${error.error} - ${error.error_description || ''}`);
    }

    // Extract tokens directly from URL hash fragment (implicit flow)
    const tokens = this.extractTokensFromURL(url);
    if (!tokens) {
      throw new Error('No tokens found in URL');
    }

    // Get user information
    const user = await this.getUserInfo(tokens.access_token);

    // Get user photo if available
    const photoURL = await this.getUserPhoto(tokens.access_token);
    if (photoURL) {
      user.photoURL = photoURL;
    }

    return { user, tokens };
  }

  /**
   * Generate random state parameter
   */
  private generateState(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  /**
   * Generate random nonce parameter
   */
  private generateNonce(): string {
    return Math.random().toString(36).substring(2, 15);
  }

  /**
   * Generate PKCE code verifier
   */
  private generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    if (typeof window !== 'undefined' && window.crypto) {
      window.crypto.getRandomValues(array);
    } else {
      // Fallback for non-browser environments
      for (let i = 0; i < array.length; i++) {
        array[i] = Math.floor(Math.random() * 256);
      }
    }
    return Array.from(array, (byte) => {
      return (byte & 0x0f).toString(16).padStart(2, '0');
    }).join('');
  }

  /**
   * Generate PKCE code challenge
   */
  private async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);

    if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
      const hash = await window.crypto.subtle.digest('SHA-256', data);
      const base64Hash = Array.from(new Uint8Array(hash))
        .map(b => String.fromCharCode(b))
        .join('');
      return btoa(base64Hash)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
    } else {
      // Fallback for non-browser environments - return a simple hash
      // This is not cryptographically secure but prevents compilation errors
      return codeVerifier.substring(0, 32);
    }
  }
} 